// useSwipeGesture.js
import { useState, useCallback, useRef, useEffect } from 'react';
import { useDrag } from '@use-gesture/react';

import { NULL_FUNCTION } from '../resource/defaults.js';

export const SWIPE_SIDE = {
  RIGHT: 'right',
  LEFT: 'left',
};

export const DEFAULT_DRAWER_WIDTH = 333; // design spec width
// A quick flick (≥ 0.5 px/ms) triggers open/close regardless of distance.
const VELOCITY_THRESHOLD = 0.5;
// A slow drag must travel ≥ 40% of the drawer width to commit.
const POSITION_THRESHOLD = 0.4;
// Minimum drag distance (px) before notifying the parent that an
// open-drag gesture is actively in progress.
const DRAG_NOTIFY_THRESHOLD_PX = DEFAULT_DRAWER_WIDTH / 2;

/**
 * Hook that manages drag gestures for a side drawer.
 *
 * Fully controlled: parent owns `isOpen` state via prop. The hook only
 * manages drag/animation state internally, and notifies the parent via
 * `onOpen` / `onClose` callbacks.
 *
 * Open gesture  — attached to `containerRef` via the `target` option.
 * Close gesture — returned as `closeBind` props to spread on the panel element.
 *
 * @param {Object}  config
 * @param {Object}  config.containerRef  React ref whose `.current` is the DOM
 *                                       element that receives the open-swipe gesture.
 * @param {boolean} config.isEnabled     Whether the open gesture is active.
 * @param {'left'|'right'} config.side   Which screen edge the drawer appears on.
 * @param {number|string}  config.width  Drawer width — number (px) or CSS string (e.g. '90vw').
 * @param {boolean} config.isOpen        Controlled open state from parent.
 * @param {Function} config.onOpen       Called when the drawer should open (gesture or programmatic).
 * @param {Function} config.onClose      Called when the drawer should close (gesture, backdrop click).
 *
 * @returns {{ drawerProps }}
 *   drawerProps  — spread onto SideDrawer (or any visual component).
 */
const useSwipeGesture = ({
  containerRef,
  isEnabled = true,
  side = SWIPE_SIDE.RIGHT,
  width = DEFAULT_DRAWER_WIDTH,
  isOpen = false,
  onOpen = NULL_FUNCTION,
  onClose = NULL_FUNCTION,
  onDragStateChange = NULL_FUNCTION,
} = {}) => {
  const [dragOffset, setDragOffset] = useState(null);
  const [dragProgress, setDragProgress] = useState(null);
  const [drawerEl, setDrawerEl] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  const onDragStateChangeRef = useRef(onDragStateChange);
  onDragStateChangeRef.current = onDragStateChange;

  // Tracks whether we've already notified the parent that an open drag is active.
  const hasFiredDragStartRef = useRef(false);

  const isDragging = dragOffset != null;
  const isOpening = isDragging && !isOpen;

  // Returns the drawer width in pixels for gesture calculations.
  // Prefers the actual rendered width; falls back to the prop or DEFAULT_DRAWER_WIDTH.
  const getDrawerWidth = useCallback(() => {
    if (drawerEl) return drawerEl.offsetWidth;
    if (typeof width === 'number') return width;
    return DEFAULT_DRAWER_WIDTH;
  }, [drawerEl, width]);

  // --- Actions ---

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // isVisible drives the CSS transform between off-screen and on-screen.
  // It is separate from isOpen so the drawer can mount at its "closed"
  // position first, then transition to "open" on the next paint.
  useEffect(() => {
    if (isOpen && drawerEl && !isDragging) {
      setIsVisible(true);
    } else if (!isOpen && !isDragging) {
      setIsVisible(false);
    }
  }, [isOpen, drawerEl, isDragging]);

  // --- Open gesture (attached to containerRef) ---
  //
  // useDrag callback params (from @use-gesture/react):
  //   down       — boolean, whether the finger is still on screen
  //   movement   — [x, y] cumulative distance (px) from touch start; destructured as [movementX]
  //   velocity   — [x, y] current swipe speed (px/ms); destructured as [velocityX]
  //   direction  — [x, y] instantaneous direction: -1 (left/up), 0 (still), 1 (right/down);
  //                destructured as [directionX]
  //   first      — boolean, true only on the very first event of this gesture
  //   cancel     — function, call to abort the gesture; no further events will fire
  //   event      — the raw DOM TouchEvent / PointerEvent

  useDrag(
    ({
      down,
      movement: [movementX],
      velocity: [velocityX],
      direction: [directionX],
      first,
      cancel,
      event,
    }) => {
      const drawerWidth = getDrawerWidth();

      if (first) {
        const isWrongDirection =
          side === SWIPE_SIDE.RIGHT ? directionX >= 0 : directionX <= 0;
        if (isWrongDirection) {
          cancel();
          return;
        }
      }

      if (event) {
        event.stopPropagation();
      }

      if (down) {
        const isWrongMovement =
          side === SWIPE_SIDE.RIGHT ? movementX >= 0 : movementX <= 0;
        if (isWrongMovement) {
          cancel();
          setDragOffset(null);
          setDragProgress(null);
          if (hasFiredDragStartRef.current) {
            hasFiredDragStartRef.current = false;
            onDragStateChangeRef.current(false);
          }
          return;
        }
        const absMovement = Math.abs(movementX);
        if (
          !hasFiredDragStartRef.current &&
          absMovement >= DRAG_NOTIFY_THRESHOLD_PX
        ) {
          hasFiredDragStartRef.current = true;
          onDragStateChangeRef.current(true);
        }
        const offset = Math.max(0, drawerWidth - absMovement);
        setDragOffset(offset);
        setDragProgress(1 - offset / drawerWidth);
      } else {
        if (hasFiredDragStartRef.current) {
          hasFiredDragStartRef.current = false;
          onDragStateChangeRef.current(false);
        }
        setDragOffset(null);
        setDragProgress(null);
        const traveled = Math.abs(movementX);
        const shouldOpen =
          velocityX > VELOCITY_THRESHOLD ||
          traveled > drawerWidth * POSITION_THRESHOLD;
        if (shouldOpen) {
          setIsVisible(true);
          onOpen();
        }
      }
    },
    {
      target: containerRef,
      enabled: isEnabled && !isOpen,
      axis: 'x',
      filterTaps: true,
      pointer: { touch: true },
      eventOptions: { passive: false },
    }
  );

  // --- Close gesture (bind-based, spread onto the panel element) ---

  const closeBind = useDrag(
    ({
      down,
      movement: [movementX],
      velocity: [velocityX],
      direction: [directionX],
      first,
      cancel,
    }) => {
      const drawerWidth = getDrawerWidth();

      if (first) {
        const isWrongDirection =
          side === SWIPE_SIDE.RIGHT ? directionX <= 0 : directionX >= 0;
        if (isWrongDirection) {
          cancel();
          return;
        }
        onDragStateChangeRef.current(true);
      }

      if (down) {
        const absMovement = Math.abs(movementX);
        const offset = Math.min(drawerWidth, Math.max(0, absMovement));
        setDragOffset(offset);
        setDragProgress(1 - offset / drawerWidth);
      } else {
        onDragStateChangeRef.current(false);
        setDragOffset(null);
        setDragProgress(null);
        const shouldClose =
          velocityX > VELOCITY_THRESHOLD ||
          Math.abs(movementX) > drawerWidth * POSITION_THRESHOLD;
        if (shouldClose) {
          setIsVisible(false);
          handleClose();
        }
      }
    },
    {
      enabled: isOpen,
      axis: 'x',
      filterTaps: true,
      pointer: { touch: true },
    }
  );

  // --- Bundle props for the visual component ---

  const drawerProps = {
    isOpen,
    isVisible,
    dragOffset,
    dragProgress,
    isDragging,
    isOpening,
    closeBind,
    handleClose,
    setDrawerEl,
    width,
    side,
  };

  return { drawerProps };
};

export default useSwipeGesture;
